import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateCaseRequestSchema,
  DECEASED_RELATIONSHIPS,
  LOSS_TYPES,
  LOSS_TYPE_LABELS,
  type CaseSummary,
  type CreateCaseRequest,
  type DeceasedRelationship,
  type HrCaseCreateFormData,
  type LossType,
} from '@besideu/shared/cases';
import { api, ApiError } from '@/lib/api.ts';
import { Button } from '@/components/ui/Button.tsx';
import { Field, Select, TextInput } from '@/components/ui/Field.tsx';

export function HrNewCase() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useQuery({
    queryKey: ['hr', 'case-create-form'],
    queryFn: () => api.get<HrCaseCreateFormData>('/api/hr/case-create-form'),
  });

  const create = useMutation({
    mutationFn: (input: CreateCaseRequest) => api.post<{ case: CaseSummary }>('/api/cases', input),
    onSuccess: async ({ case: created }) => {
      await queryClient.invalidateQueries({ queryKey: ['hr', 'cases'] });
      await navigate(`/hr/cases/${created.id}`);
    },
  });

  return (
    <section className="max-w-xl space-y-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">New support pathway</h1>
        <p className="mt-2 text-muted-foreground">
          Log a bereavement on behalf of an employee. The manager is set from the employee&rsquo;s
          team mapping; if no mapping exists, set it in Members first.
        </p>
      </header>

      {form.isPending ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : form.error ? (
        <p className="text-traffic-red">Could not load the form.</p>
      ) : form.data && form.data.employees.length === 0 ? (
        <NoEmployeesState />
      ) : (
        <NewCaseForm
          data={form.data}
          pending={create.isPending}
          submitError={errorMessage(create.error)}
          onSubmit={(input) => create.mutate(input)}
        />
      )}
    </section>
  );
}

function NewCaseForm({
  data,
  pending,
  submitError,
  onSubmit,
}: {
  data: HrCaseCreateFormData;
  pending: boolean;
  submitError: string | null;
  onSubmit: (input: CreateCaseRequest) => void;
}) {
  const firstEmployeeId = data.employees[0]?.id ?? '';
  const [employeeId, setEmployeeId] = useState(firstEmployeeId);
  const [relationship, setRelationship] = useState<DeceasedRelationship>('Parent');
  // '' = not recorded (optional — spec §19 avoids forcing detail).
  const [lossType, setLossType] = useState<LossType | ''>('');
  const [dateOfLoss, setDateOfLoss] = useState(today());
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<string, string>>>({});

  const selectedEmployee = useMemo(
    () => data.employees.find((e) => e.id === employeeId) ?? null,
    [data.employees, employeeId],
  );
  const isMapped =
    selectedEmployee?.mappedManagerId !== null && selectedEmployee?.mappedManagerId !== undefined;

  function handle(e: FormEvent) {
    e.preventDefault();
    const payload: CreateCaseRequest = {
      employeeId,
      deceasedRelationship: relationship,
      // Omit entirely when not recorded so the field stays optional/null.
      ...(lossType ? { lossType } : {}),
      dateOfLoss,
    };
    const parsed = CreateCaseRequestSchema.safeParse(payload);
    if (!parsed.success) {
      const next: Partial<Record<string, string>> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as string;
        if (!next[key]) next[key] = issue.message;
      }
      setFieldErrors(next);
      return;
    }
    setFieldErrors({});
    onSubmit(parsed.data);
  }

  return (
    <form onSubmit={handle} className="grid gap-5 rounded-lg border border-border p-6">
      <Field label="Employee" error={fieldErrors.employeeId}>
        {(id, descProps) => (
          <Select
            id={id}
            {...descProps}
            required
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
          >
            {data.employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.name}
                {emp.mappedManagerId ? '' : ' (no manager mapping)'}
              </option>
            ))}
          </Select>
        )}
      </Field>

      {selectedEmployee && !isMapped ? (
        <div
          className="rounded-md border border-traffic-amber/40 bg-traffic-amber/10 px-4 py-3 text-sm text-foreground"
          role="status"
        >
          <p className="font-medium">No manager mapping</p>
          <p className="mt-1">
            Set the employee&rsquo;s manager in{' '}
            <Link className="underline" to={`/hr/members?employee=${selectedEmployee.id}`}>
              Members
            </Link>{' '}
            before opening this support pathway.
          </p>
        </div>
      ) : null}

      <Field
        label="Relationship to the deceased"
        error={fieldErrors.deceasedRelationship}
        hint="Used to tailor policy guidance — does not appear in employee-facing chat."
      >
        {(id, descProps) => (
          <Select
            id={id}
            {...descProps}
            value={relationship}
            onChange={(e) => setRelationship(e.target.value as DeceasedRelationship)}
          >
            {DECEASED_RELATIONSHIPS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field
        label="Type of loss (optional)"
        hint="Helps tailor support for specific kinds of loss. Leave as “Not recorded” if unknown or the employee would rather not say."
      >
        {(id, descProps) => (
          <Select
            id={id}
            {...descProps}
            value={lossType}
            onChange={(e) => setLossType(e.target.value as LossType | '')}
          >
            <option value="">Not recorded</option>
            {LOSS_TYPES.map((t) => (
              <option key={t} value={t}>
                {LOSS_TYPE_LABELS[t]}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field label="Date of loss" error={fieldErrors.dateOfLoss}>
        {(id, descProps) => (
          <TextInput
            id={id}
            {...descProps}
            type="date"
            required
            max={today()}
            value={dateOfLoss}
            onChange={(e) => setDateOfLoss(e.target.value)}
          />
        )}
      </Field>

      <div>
        <Button type="submit" loading={pending} disabled={selectedEmployee !== null && !isMapped}>
          Create support pathway
        </Button>
        {submitError ? (
          <p className="mt-2 text-sm text-traffic-red" role="alert">
            {submitError}
          </p>
        ) : null}
      </div>
    </form>
  );
}

function NoEmployeesState() {
  return (
    <div className="rounded-lg border border-dashed border-border bg-muted/40 p-6">
      <p className="text-muted-foreground">
        No employees in the organisation yet. Invite managers and employees from Members before
        opening a support pathway.
      </p>
    </div>
  );
}

function errorMessage(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    if (err.status === 404 && err.message === 'employee_not_found') {
      return 'That employee was not found in your organisation.';
    }
    if (err.status === 422 && err.message === 'employee_unmapped') {
      return 'That employee has no manager mapping. Set it in Members first.';
    }
    if (err.status === 400) return 'Please check the form and try again.';
    if (err.status === 403) return 'You don’t have permission to create this support pathway.';
    return 'Something went wrong creating the support pathway.';
  }
  return 'Network error.';
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}
